import { useEffect, useRef } from 'react'

import { fetchAnomalies } from '../../anomalies/anomaliesSlice'
import { fetchLocations } from '../../locations/locationsSlice'
import { fetchUsers } from '../../users/usersSlice'
import {
  fetchAllClosedCards,
  fetchCardsForDateRange,
  fetchClosedCards,
  fetchOpenCards,
  reload,
} from '../cardsSlice'
import { selectAnomaliesStatus } from '../../anomalies/anomaliesSelectors'
import { selectLocationsStatus } from '../../locations/locationsSelectors'
import { selectUsersStatus } from '../../users/usersSelectors'
import {
  selectClosedCardsStatus,
  selectFromDateFilter,
  selectOpenCardsStatus,
  selectToDateFilter,
} from '../cardsSelectors'
import { useAppDispatch, useAppSelector } from '../../store/hooks'

const normalizeOptions = (limitClosedCardsOrOptions) => {
  if (limitClosedCardsOrOptions !== null && typeof limitClosedCardsOrOptions === 'object') {
    return {
      limitClosedCards: limitClosedCardsOrOptions.limitClosedCards,
      closedWithinDays:
        typeof limitClosedCardsOrOptions.closedWithinDays === 'number'
          ? limitClosedCardsOrOptions.closedWithinDays
          : null,
      filterByDateRange: Boolean(limitClosedCardsOrOptions.filterByDateRange),
    }
  }

  return {
    limitClosedCards: limitClosedCardsOrOptions,
    closedWithinDays: null,
    filterByDateRange: false,
  }
}

const useFetchCardsData = (limitClosedCardsOrOptions) => {
  const dispatch = useAppDispatch()
  const { limitClosedCards, closedWithinDays, filterByDateRange } = normalizeOptions(
    limitClosedCardsOrOptions
  )

  const anomaliesStatus = useAppSelector(selectAnomaliesStatus)
  const locationsStatus = useAppSelector(selectLocationsStatus)
  const usersStatus = useAppSelector(selectUsersStatus)
  const openCardsStatus = useAppSelector(selectOpenCardsStatus)
  const closedCardsStatus = useAppSelector(selectClosedCardsStatus)
  const fromDateFilter = useAppSelector(selectFromDateFilter)
  const toDateFilter = useAppSelector(selectToDateFilter)

  const prevFilterByDateRangeRef = useRef(null)
  const resetDateRangeDatasetRef = useRef(false)
  const lastFetchedDateRangeKey = useAppSelector((s) => s.cards.lastFetchedDateRangeKey)
  const failedDateRangeKey = useAppSelector((s) => s.cards.failedDateRangeKey)

  useEffect(() => {
    if (prevFilterByDateRangeRef.current === null) {
      prevFilterByDateRangeRef.current = filterByDateRange
      return
    }
    if (prevFilterByDateRangeRef.current !== filterByDateRange) {
      dispatch(reload())
      prevFilterByDateRangeRef.current = filterByDateRange
    }
  }, [dispatch, filterByDateRange])

  useEffect(() => {
    if (filterByDateRange) {
      resetDateRangeDatasetRef.current = false
      return
    }
    if (lastFetchedDateRangeKey && !resetDateRangeDatasetRef.current) {
      resetDateRangeDatasetRef.current = true
      dispatch(reload())
    }
  }, [dispatch, filterByDateRange, lastFetchedDateRangeKey])

  useEffect(() => {
    const fetchData = async () => {
      if (anomaliesStatus === 'idle') dispatch(fetchAnomalies())
      if (usersStatus === 'idle') dispatch(fetchUsers())
      if (locationsStatus === 'idle') dispatch(fetchLocations())

      if (filterByDateRange) {
        if (!fromDateFilter || !toDateFilter) return
        if (openCardsStatus === 'loading' || closedCardsStatus === 'loading') return
        const rangeKey = `${fromDateFilter}|${toDateFilter}`
        if (
          openCardsStatus === 'succeeded' &&
          closedCardsStatus === 'succeeded' &&
          lastFetchedDateRangeKey === rangeKey
        ) {
          return
        }
        if (
          openCardsStatus === 'failed' &&
          closedCardsStatus === 'failed' &&
          failedDateRangeKey === rangeKey
        ) {
          return
        }
        dispatch(fetchCardsForDateRange({ fromDate: fromDateFilter, toDate: toDateFilter }))
        return
      }

      if (openCardsStatus === 'idle') {
        dispatch(fetchOpenCards())
      }
      if (closedCardsStatus === 'idle') {
        if (limitClosedCards) {
          if (closedWithinDays != null) {
            dispatch(fetchClosedCards({ closedWithinDays }))
          } else {
            dispatch(fetchClosedCards())
          }
        } else {
          dispatch(fetchAllClosedCards())
        }
      }
    }
    fetchData()
  }, [
    dispatch,
    filterByDateRange,
    anomaliesStatus,
    locationsStatus,
    usersStatus,
    openCardsStatus,
    closedCardsStatus,
    fromDateFilter,
    toDateFilter,
    limitClosedCards,
    closedWithinDays,
    lastFetchedDateRangeKey,
    failedDateRangeKey,
  ])

  if (
    anomaliesStatus === 'failed' ||
    usersStatus === 'failed' ||
    locationsStatus === 'failed'
  ) {
    return 'failed'
  }

  if (filterByDateRange) {
    if (openCardsStatus === 'failed' && closedCardsStatus === 'failed') return 'failed'
  } else if (openCardsStatus === 'failed' || closedCardsStatus === 'failed') {
    return 'failed'
  }

  return openCardsStatus === 'succeeded' && closedCardsStatus === 'succeeded'
    ? 'succeeded'
    : 'loading'
}

export default useFetchCardsData
